import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { handleAuthError } from './authUtils';

// Breadcrumbs component
const Breadcrumbs = () => {
  const location = window.location.pathname;
  const segments = location.split('/').filter(seg => seg);
  
  const crumbs = segments.map(seg => {
    const decodedSeg = decodeURIComponent(seg);
    return decodedSeg;
  });
  
  return (
    <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', textAlign: 'center', letterSpacing: 1 }}>
      {crumbs.map((seg, idx) => {
        let displayText;
        if (idx === 0 && seg === 'mecz') {
          displayText = 'menu';
        } else if (seg === 'turniej-rankingowy') {
          displayText = 'turniej rankingowy';
        } else if (seg === 'dodaj') {
          displayText = 'dodaj turniej';
        } else if (seg === 'szczegoly') {
          // Check if we came from the add tournament page by looking at URL parameters
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('from') === 'dodaj') {
            displayText = 'dodaj turniej';
          } else {
            displayText = 'szczegóły turnieju';
          }
        } else if (seg === 'zakoncz') {
          displayText = 'zakończ turniej';
        } else if (seg === 'mecz') {
          // Skip the intermediate 'mecz' segment
          return null;
        } else if (seg.match(/^\d+$/)) {
          // For numeric segments (tournament IDs), show "rundy"
          displayText = 'rundy';
        } else {
          displayText = seg;
        }
        
        if (displayText === null) return null;
        
        return (
          <span key={idx}>
            {idx > 0 && <span style={{ margin: '0 8px' }}>/</span>}
            <span>{displayText}</span>
          </span>
        );
      }).filter(Boolean)}
    </div>
  );
};

const BackButton = ({ onClick }) => (
  <button
    onClick={onClick}
    style={{
      background: 'none',
      border: 'none',
      fontSize: 24,
      cursor: 'pointer',
      color: '#888',
      padding: 8,
      borderRadius: 4,
      transition: 'color 0.2s',
      textDecoration: 'none',
    }}
    title="Wróć"
  >
    ←
  </button>
);

const FinishTournament = () => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [formData, setFormData] = useState({
    totalplayers: '',
    place: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    console.log('useEffect triggered, tournamentId:', tournamentId);
    
    // Test if backend is reachable
    const testBackend = async () => {
      try {
        console.log('Testing backend connectivity...');
        const response = await fetch('/api/tournaments/open', {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('Backend test response status:', response.status);
        if (response.ok) {
          console.log('Backend is reachable');
        } else {
          console.error('Backend returned error:', response.status);
        }
      } catch (err) {
        console.error('Backend test failed:', err);
      }
    };
    
    testBackend();
    
    if (tournamentId) {
      fetchTournamentDetails();
    } else {
      console.error('tournamentId is undefined!');
      setError('Błąd: Nieprawidłowy identyfikator turnieju');
    }
  }, [tournamentId]);

  const fetchTournamentDetails = async () => {
    try {
      const token = sessionStorage.getItem('access_token');
      const url = `/api/tournaments/${tournamentId}`;
      console.log('Fetching tournament from URL:', url);
      console.log('Tournament ID:', tournamentId);
      console.log('Token exists:', !!token);
      console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'No token');
      
      if (!token) {
        setError('Brak tokenu autoryzacji. Zaloguj się ponownie.');
        return;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      
      if (response.status === 401) {
        handleAuthError(response);
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        console.log('Tournament data received:', data);
        setTournament(data);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setError(`Nie udało się pobrać danych turnieju (${response.status})`);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Błąd połączenia z serwerem');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = sessionStorage.getItem('access_token');
      
      if (!token) {
        setError('Brak tokenu autoryzacji. Zaloguj się ponownie.');
        setLoading(false);
        return;
      }
      
      const requestData = {
        isfinished: true,
        totalplayers: formData.totalplayers ? parseInt(formData.totalplayers) : null,
        place: formData.place ? parseInt(formData.place) : null
      };
      
      const response = await fetch(`/api/tournaments/${tournamentId}/finish`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.status === 401) {
        setError('Sesja wygasła. Zaloguj się ponownie.');
        setLoading(false);
        return;
      }

      if (response.ok) {
        // Always navigate to open tournaments list after finishing
        navigate('/mecz/turniej-rankingowy/dodaj');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Nie udało się zakończyć turnieju');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromParam = urlParams.get('from');
    
    if (fromParam === 'dodaj') {
      let url = `/mecz/turniej-rankingowy/szczegoly/${tournamentId}`;
      url += `?from=${fromParam}`;
      navigate(url);
    } else {
      navigate(`/mecz/turniej-rankingowy/szczegoly/${tournamentId}`);
    }
  };

  if (!tournament) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Ładowanie...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* ShootMe Title */}
      <h1 style={{
        fontSize: '2.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        ShootMe
      </h1>
      
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton onClick={handleCancel} />
        <Breadcrumbs />
      </div>

      <div style={{ padding: '20px' }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '30px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '600px',
          margin: '0 auto'
        }}>

          <h1 style={{
            color: '#2d3748',
            fontSize: '28px',
            fontWeight: '700',
            marginBottom: '30px',
            textAlign: 'center',
            fontFamily: 'Nunito, Arial, sans-serif'
          }}>
            Zakończ turniej
          </h1>

          <div style={{
            backgroundColor: '#f7fafc',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '30px',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{
              color: '#2d3748',
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '10px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}>
              Informacje o turnieju
            </h3>
            <p style={{ color: '#4a5568', margin: '5px 0' }}>
              <strong>Nazwa:</strong> {tournament.tournamentname}
            </p>
            <p style={{ color: '#4a5568', margin: '5px 0' }}>
              <strong>Data:</strong> {new Date(tournament.tournamentdate).toLocaleDateString('pl-PL')}
            </p>
            <p style={{ color: '#4a5568', margin: '5px 0' }}>
              <strong>Miasto:</strong> {tournament.city}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '25px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#2d3748',
                fontFamily: 'Nunito, Arial, sans-serif'
              }}>
                Liczba drużyn w turnieju
              </label>
              <input
                type="number"
                name="totalplayers"
                value={formData.totalplayers}
                onChange={handleInputChange}
                min="1"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#2d3748',
                fontFamily: 'Nunito, Arial, sans-serif'
              }}>
                Zajęte miejsce
              </label>
              <input
                type="number"
                name="place"
                value={formData.place}
                onChange={handleInputChange}
                min="1"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            <div style={{
              backgroundColor: '#fef3c7',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '30px',
              border: '1px solid #f59e0b'
            }}>
              <p style={{
                color: '#92400e',
                fontSize: '14px',
                margin: '0',
                fontFamily: 'Nunito, Arial, sans-serif'
              }}>
                <strong>Uwaga:</strong> Powyższe informacje nie są obowiązkowe i mogą być edytowane po zamknięciu turnieju.
              </p>
            </div>

            {error && (
              <div style={{
                backgroundColor: '#fed7d7',
                color: '#c53030',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #feb2b2',
                fontFamily: 'Nunito, Arial, sans-serif'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'background 0.2s',
                  minWidth: '120px'
                }}
                onMouseOver={(e) => e.target.style.background = '#5a6268'}
                onMouseOut={(e) => e.target.style.background = '#6c757d'}
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  background: loading ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'background 0.2s',
                  minWidth: '120px'
                }}
                onMouseOver={(e) => {
                  if (!loading) {
                    e.target.style.background = '#b91c1c';
                  }
                }}
                onMouseOut={(e) => {
                  if (!loading) {
                    e.target.style.background = '#dc2626';
                  }
                }}
              >
                {loading ? 'Zapisywanie...' : 'Zakończ turniej'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FinishTournament;
